import React, { useState } from 'react';
import { Icon } from '../../Icon';
import { parseBrazilianNumber, maskNumber, maskInteger } from '../../../utils/formatUtils';

// Componente de Campo com Validação
export const FormField: React.FC<{
  label: string;
  placeholder?: string;
  value: string;
  onChange: (value: string) => void;
  type?: 'number' | 'select' | 'text' | 'date' | 'textarea';
  helpText?: string;
  required?: boolean;
  min?: number;
  max?: number;
  step?: string;
  options?: { value: string; label: string }[];
  isNumeric?: boolean;
  isInteger?: boolean;
}> = ({
  label,
  placeholder,
  value,
  onChange,
  type = 'number',
  helpText,
  required = false,
  min,
  max,
  step = 'any',
  options,
  isNumeric = false,
  isInteger = false
}) => {
    const [error, setError] = useState('');

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
      let newValue = e.target.value;

      if (isNumeric && type === 'text') {
        newValue = maskNumber(newValue);
      } else if (isInteger && type === 'text') {
        newValue = maskInteger(newValue);
      }

      if (e.target.value !== newValue) {
        e.target.value = newValue;
      }

      onChange(newValue);

      const numericValue = isNumeric ? parseFloat(parseBrazilianNumber(newValue)) : parseFloat(newValue);

      if (required && !newValue) {
        setError('Este campo é obrigatório');
      } else if (isNumeric && min !== undefined && numericValue < min) {
        setError(`Informe um valor maior que ${min}`);
      } else if (isNumeric && max !== undefined && numericValue > max) {
        setError(`Informe um valor menor que ${max}`);
      } else {
        setError('');
      }
    };

    return (
      <div className="space-y-2">
        <label className="text-xs font-black text-theme-muted uppercase tracking-widest flex items-center gap-1">
          {label}
          {required && <span className="text-rose-500">*</span>}
        </label>
        {type === 'select' ? (
          <select
            value={value}
            onChange={handleChange}
            className={`w-full bg-theme-input border-2 rounded-xl py-3 px-4 font-bold transition-all outline-none ${error
              ? 'border-rose-300 bg-rose-950/20'
              : 'border-theme-border focus:border-indigo-400'
              }`}
          >
            <option value="">Selecione...</option>
            {options?.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        ) : (
          <input
            type={(isNumeric || isInteger) ? 'text' : type}
            value={value}
            onChange={handleChange}
            placeholder={placeholder}
            className={`w-full bg-theme-input border-2 rounded-xl py-3 px-4 font-bold transition-all outline-none ${error
              ? 'border-rose-300 bg-rose-950/20'
              : 'border-theme-border focus:border-indigo-400'
              }`}
          />
        )}
        {type === 'textarea' && (
          <textarea
            value={value}
            onChange={handleChange as any}
            placeholder={placeholder}
            className={`w-full bg-theme-input border-2 rounded-xl py-3 px-4 font-bold transition-all outline-none text-theme-text resize-none ${error
              ? 'border-rose-300 bg-rose-950/20'
              : 'border-theme-border focus:border-indigo-400'
              }`}
            rows={4}
          />
        )}
        {helpText && !error && (
          <p className="text-xs text-theme-muted italic">{helpText}</p>
        )}
        {error && (
          <p className="text-xs text-rose-500 font-medium">{error}</p>
        )}
      </div>
    );
  };

// Componente de Resultado Detalhado
export const ResultCard: React.FC<{
  title: string;
  value: string | number;
  description?: string;
  color?: 'indigo' | 'emerald' | 'rose' | 'amber' | 'slate' | 'blue' | 'green' | 'purple' | 'orange';
}> = ({
  title,
  value,
  description,
  color = 'indigo'
}) => {
    const colorClasses = {
      indigo: 'bg-accent-gradient text-white shadow-accent',
      emerald: 'bg-emerald-600 text-white shadow-emerald-200',
      rose: 'bg-rose-600 text-white shadow-rose-200',
      amber: 'bg-amber-600 text-white shadow-amber-200',
      slate: 'bg-slate-600 text-white shadow-slate-200',
      blue: 'bg-blue-600 text-white shadow-blue-200',
      green: 'bg-green-600 text-white shadow-green-200',
      purple: 'bg-purple-600 text-white shadow-purple-200',
      orange: 'bg-orange-600 text-white shadow-orange-200'
    };

    return (
      <div className={`p-8 rounded-[2.5rem] shadow-xl text-center ${colorClasses[color]}`}>
        <p className="text-xs font-black opacity-60 uppercase tracking-widest mb-2">{title}</p>
        <p className="text-4xl font-[1000] mb-3">{value}</p>
        {description && (
          <p className="text-sm opacity-90">{description}</p>
        )}
      </div>
    );
  };

// Componente de Bloco Educativo
export const EducationalBlock = ({
  title,
  tips,
  warnings
}: {
  title: string;
  tips: string[];
  warnings?: string[];
}) => (
  <div className="bg-theme-muted-section rounded-2xl p-6 border border-theme-border">
    <h3 className="text-lg font-bold text-theme-text mb-4 flex items-center gap-2">
      <Icon name="zap" className="w-5 h-5 text-amber-500" />
      {title}
    </h3>
    <div className="space-y-3">
      {tips.map((tip, index) => (
        <div key={index} className="flex gap-3">
          <div className="w-2 h-2 bg-emerald-500 rounded-full mt-2 flex-shrink-0" />
          <p className="text-sm text-theme-muted">{tip}</p>
        </div>
      ))}
      {warnings && warnings.map((warning, index) => (
        <div key={index} className="flex gap-3">
          <div className="w-2 h-2 bg-amber-500 rounded-full mt-2 flex-shrink-0" />
          <p className="text-sm text-theme-muted">{warning}</p>
        </div>
      ))}
    </div>
  </div>
);
